import { GOOGLE_AUTOMATION_STAGE_STATUSES } from './googleAutomationStageStatuses'
import * as googleUrls from './googleUrls'
import { GoogleSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { StageType, GoogleRecommendationKey } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import SectionNotFoundException from '../exception/sectionNotFoundException'
import {
  AutomationErrors,
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { getElement, shouldUpdateSetting } from '../utils'

const getCurrentValueFn = (key: GoogleSettingKey) =>
  getCurrentValueFnFromKey(key, (value) => value)

const getIsFulfilledFn = (key: GoogleSettingKey) =>
  getIsFulfilledFnFromKey(key, (value) => value === 'Off')

const getCantUndoFn = () => defaultCantUndoFn

function getIsDisabledFn(settingKey: GoogleSettingKey) {
  return (
    automationErrors: Partial<Record<GoogleSettingKey, AutomationErrors>>,
  ) => getErrorIsDisabledAndReasonFn(settingKey)(automationErrors).isDisabled
}

function getIsDisabledReasonFn(settingKey: GoogleSettingKey) {
  return (
    automationErrors: Partial<Record<GoogleSettingKey, AutomationErrors>>,
  ) =>
    getErrorIsDisabledAndReasonFn(settingKey)(automationErrors).isDisabledReason
}

const settings = [
  {
    key: GoogleSettingKey.PAYMENTS_SHARING,
    label:
      'Share that you have Google Pay with companies outside Google setting',
  },
  {
    key: GoogleSettingKey.CREDITWORTHINESS_SHARING,
    label:
      'Share info about your creditworthiness with other Google companies setting',
  },
  {
    key: GoogleSettingKey.MARKETING_SHARING,
    label:
      'Share your personal info with other Google companies for marketing purposes setting',
  },
  {
    key: GoogleSettingKey.PERSONALIZATION,
    label: 'Personalization within Google Pay setting',
  },
]

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'GATHER_GOOGLE_PAYMENTS',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.managePaymentsDataUrl.navigateForUser(googleAccountIndex)

      // Users located where Google Wallet isn't available will be redirected
      // to https://wallet.google.com/wallet/forbidden
      if (window.location.pathname === '/wallet/forbidden') {
        for (const setting of settings) {
          await userContext.updateStoredAutomationErrors(
            {
              [setting.key]: {
                gatherError: {
                  message: "Google Wallet isn't available in user's location",
                  name: SectionNotFoundException.exceptionName,
                },
              },
            },
            GoogleRecommendationKey.GOOGLE_PAYMENTS,
          )
        }
        // No payments settings are available so unset them all from initial
        // values. This tells update and undo that they're disabled.
        await userContext.updateStoredInitialValues(
          {},
          GoogleRecommendationKey.GOOGLE_PAYMENTS,
        )
        return
      }
      // Users with kid/teen Google accounts will be redirected to
      // https://families.google.com/service-restricted?es=...&continue=...
      if (window.location.pathname.startsWith('/service-restricted')) {
        for (const setting of settings) {
          await userContext.updateStoredAutomationErrors(
            {
              [setting.key]: {
                gatherError: {
                  message:
                    "Google Wallet isn't available for kid/teen Google accounts",
                  name: SectionNotFoundException.exceptionName,
                },
              },
            },
            GoogleRecommendationKey.GOOGLE_PAYMENTS,
          )
        }
        await userContext.updateStoredInitialValues(
          {},
          GoogleRecommendationKey.GOOGLE_PAYMENTS,
        )
        return
      }
      const values: Partial<Record<GoogleSettingKey, string>> = {}
      for (const setting of settings) {
        let currentValue
        try {
          const switchEl = await getElement<HTMLDivElement>(
            document,
            `//button[@role="switch"][@aria-label="${setting.label}"]`,
            {
              timeout: 3000,
            },
          )
          currentValue = switchEl.ariaChecked === 'true' ? 'On' : 'Off'
        } catch {
          await userContext.updateStoredAutomationErrors(
            {
              [setting.key]: {
                gatherError: {
                  message: `${setting.label} isn't available`,
                  name: SectionNotFoundException.exceptionName,
                },
              },
            },
            GoogleRecommendationKey.GOOGLE_PAYMENTS,
          )
        } finally {
          values[setting.key] = currentValue
          await userContext.updateStoredInitialValues(
            values,
            GoogleRecommendationKey.GOOGLE_PAYMENTS,
          )
        }
      }
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'UPDATE_GOOGLE_PAYMENTS',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.managePaymentsDataUrl.navigateForUser(googleAccountIndex)

      for (const setting of settings) {
        if (
          !shouldUpdateSetting(
            userContext.settingsSelected,
            userContext.initialValues,
            setting.key,
            getIsFulfilledFn,
          )
        ) {
          // eslint-disable-next-line no-continue
          continue
        }
        let switchEl = await getElement<HTMLDivElement>(
          document,
          `//button[@role="switch"][@aria-label="${setting.label}"]`,
        )
        const currentValue = switchEl.ariaChecked === 'true' ? 'On' : 'Off'
        const targetValue = 'Off'
        if (currentValue !== targetValue && !userContext.isDry) {
          switchEl.click()
          switchEl = await getElement<HTMLDivElement>(
            document,
            `//button[@role="switch"][@aria-label="${setting.label}"][@aria-checked="false"]`,
          )
        }
      }
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'UNDO_GOOGLE_PAYMENTS',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.managePaymentsDataUrl.navigateForUser(googleAccountIndex)

      for (const setting of settings) {
        if (
          !shouldUpdateSetting(
            userContext.settingsSelected,
            userContext.initialValues,
            setting.key,
            getIsFulfilledFn,
          )
        ) {
          // eslint-disable-next-line no-continue
          continue
        }
        let switchEl = await getElement<HTMLDivElement>(
          document,
          `//button[@role="switch"][@aria-label="${setting.label}"]`,
        )
        const currentValue = switchEl.ariaChecked === 'true' ? 'On' : 'Off'
        const initialValue = userContext.initialValues?.[setting.key]
        if (currentValue !== initialValue && !userContext.isDry) {
          switchEl.click()
          switchEl = await getElement<HTMLDivElement>(
            document,
            `//button[@role="switch"][@aria-label="${setting.label}"][@aria-checked="true"]`,
          )
        }
      }
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
